// PassShelf.jsx
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGifts, faUserClock, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';

import ExamUnionEarningCalculator from './ExamUnionEarningCalculator';

const PERIOD_OPTIONS = [
  "Lifetime", "Today", "Yesterday", "Current Week",
  "Current Month", "Current Quarter", "Current Year", "Custom Date Range"
];

const PassShelfList = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const { fetchData } = useFetch();

  // User Info
  const userId = authState.user?._id;
  const myUserCode = authState.user?.think5whyscode;

  // Core Data
  const [myPasses, setMyPasses] = useState([]);
  const [selectedPassId, setSelectedPassId] = useState(null);

  // UI States
  const [showTable, setShowTable] = useState(false);
  const [instantReferralViewClicked, setInstantReferralViewClicked] = useState(false);
  const [viewBillClicked, setViewBillClicked] = useState(false);
  const [showReferralsList, setShowReferralsList] = useState(false);
  const [downTheLineReferralsDataClicked, setDownTheLineReferralsDataClicked] = useState(false);

  // Data States
  const [myBBList, setMyBBList] = useState([]);
  const [myInvoice, setMyInvoice] = useState(null);
  const [myReferralsList, setMyReferralsList] = useState([]);
  const [downTheLineReferralsList, setDownTheLineReferralsList] = useState([]);

  // Filters
  const [selectedPeriod, setSelectedPeriod] = useState("Lifetime");
  const [selectedLevel, setSelectedLevel] = useState("All");
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch My Passes
  const fetchMyPasses = useCallback(() => {
    const config = {
      url: "/myexamunionpasses",
      method: "get",
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response?.mypasss && Array.isArray(response.mypasss)) {
          setMyPasses(response.mypasss);
        }
      })
      .catch((error) => console.error("Error fetching passes:", error));
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchMyPasses();
  }, [authState.isLoggedIn, fetchMyPasses]);

  // Formatters
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString('en-IN', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
  };

  // Navigation Handler
  const handleReadClick = (pass) => {
    const state = {
      passId: pass.passCode,
      passMasterId: pass.passMasterId,
      passCollectionName: pass.passCollectionName,
      lastPageRead: pass.lastPageRead,
      passPages: pass.passPages,
      isChildren: pass.passIsChildren,
    };

    if (pass.passCode === "ThinkNoteFlex") navigate('/readnoteflex', { state });
    else if (pass.passCode === "CAFE") navigate('/readcafepass', { state });
    else if (pass.passCode === "MOPass") navigate('/readopentextpass', { state });
    else navigate('/readpass', { state });
  };

  // Instant Referrals
  const fetchMyInstantReferral = useCallback((passId) => {
    const config = {
      url: "/myexamunionpasses/mybblist",
      method: "post",
      headers: { Authorization: authState.token },
      data: { passId, bbId: userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response?.myBBList) setMyBBList(response.myBBList);
      })
      .catch((err) => console.error(err));
  }, [authState.token, fetchData, userId]);

  const handleInstantReferralsListClick = (passId) => {
    setInstantReferralViewClicked((prev) => !prev);
    if (!instantReferralViewClicked) {
      fetchMyInstantReferral(passId);
    }
  };

  // Invoice
  const fetchMyInvoice = useCallback((passId) => {
    const config = {
      url: "/myorders/invoice",
      method: "post",
      headers: { Authorization: authState.token },
      data: { passId, userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => setMyInvoice(response?.myInvoice || null))
      .catch(() => setMyInvoice(null));
  }, [authState.token, fetchData, userId]);

  const handleViewBillClick = (passId) => {
    setViewBillClicked((prev) => !prev);
    if (!myInvoice) fetchMyInvoice(passId);
  };

  // Direct Referrals
  const fetchMyReferralsList = useCallback((passId, userCode) => {
    const config = {
      url: "/mypasss/mydirectreferralslist",
      method: "post",
      headers: { Authorization: authState.token },
      data: { passId, advisorCode: userCode, userCode },
    };

    fetchData(config)
      .then((res) => setMyReferralsList(res?.referralsList || []))
      .catch(console.error);
  }, [authState.token, fetchData]);

  const handleMyReferralsListClick = (pass) => {
    setSelectedPassId(pass.passId);
    setShowReferralsList((prev) => !prev);
    if (!showReferralsList) {
      fetchMyReferralsList(pass.passId, pass.userCode);
    }
  };

  // Down The Line Referrals
  const fetchDownTheLineReferralsList = useCallback((passId) => {
    const config = {
      url: "/examuinonmlmchart/downthelinereferralslist",
      method: "post",
      headers: { Authorization: authState.token },
      data: { passId, userCode: myUserCode, userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((res) => setDownTheLineReferralsList(res?.referralsList || []))
      .catch(console.error);
  }, [authState.token, fetchData, myUserCode, userId]);

  const handleGetDownTheLineReferralList = (passId) => {
    setDownTheLineReferralsDataClicked(true);
    fetchDownTheLineReferralsList(passId);
  };

  // Memoized Computations
  const filteredList = useMemo(() => {
    if (!downTheLineReferralsList.length) return [];

    const now = new Date();
    let start = null, end = null;

    switch (selectedPeriod) {
      case "Today":
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      case "Yesterday":
        start = new Date(now);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setHours(23, 59, 59, 999);
        break;
      // Add more cases as needed...
      default:
        return downTheLineReferralsList;
    }

    return downTheLineReferralsList.filter(item => {
      const itemDate = new Date(item.passBuyDate);
      const levelMatch = selectedLevel === "All" || item.matchedLevel === Number(selectedLevel);
      const dateMatch = !start || (itemDate >= start && itemDate <= end);
      return levelMatch && dateMatch;
    });
  }, [downTheLineReferralsList, selectedPeriod, selectedLevel]);

  const aggregatedLevels = useMemo(() => {
    const levels = {};
    filteredList.forEach(item => {
      const lvl = item.matchedLevel;
      if (!levels[lvl]) levels[lvl] = { count: 0, totalCom: 0 };
      levels[lvl].count += 1;
      levels[lvl].totalCom += item.commissionEarned || 0;
    });
    return levels;
  }, [filteredList]);

  // Sort passes by purchase date (newest first)
  const sortedPasses = useMemo(() => {
    return [...myPasses].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
  }, [myPasses]);

  const scrollToDetail = (passCode) => {
    document.getElementById(`pass-detail-${passCode}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-black border-b border-blue-500 py-4 z-10">
        <h1 className="text-center text-2xl lg:text-3xl font-bold text-yellow-200">
          My PassShelf
        </h1>
      </div>

      {/* Pass Count */}
      <div className="px-4 py-6 text-center">
        {myPasses.length === 0 ? (
          <p className="text-yellow-300 text-lg">You don't have any passes yet.</p>
        ) : (
          <p className="text-lg text-white">
            You have <span className="font-semibold text-yellow-200">{myPasses.length}</span> passes
          </p>
        )}
      </div>

      {/* Pass Thumbnails Grid - Mobile First */}
      <div className="px-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {sortedPasses.map((pass) => (
          <div
            key={pass.passCode}
            onClick={() => scrollToDetail(pass.passCode)}
            className="bg-zinc-900 border border-blue-400/50 rounded-xl p-4 cursor-pointer hover:border-blue-400 transition-all active:scale-95"
          >
            <div className="text-center">
              <h3 className="text-xs lg:text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                {pass.passTitle}
              </h3>
              {pass.isSampleBuy && (
                <span className="inline-block mt-2 text-[10px] bg-amber-500 text-black px-2 py-0.5 rounded">
                  SAMPLE
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pass Details */}
      <div className="px-4 mt-8 space-y-10">
        {sortedPasses.map((pass, index) => (
          <div
            key={pass.passCode}
            id={`pass-detail-${pass.passCode}`}
            className="bg-zinc-900 border border-blue-400/30 rounded-2xl p-5 lg:p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="text-blue-400 text-xs">#{index + 1}</span>
                <h2 className="text-lg lg:text-xl font-semibold text-yellow-100 mt-1">
                  {pass.passTitle}
                </h2>
              </div>
              {pass.isGifted === 1 && (
                <div className="text-3xl text-red-500">
                  <FontAwesomeIcon icon={faGifts} />
                </div>
              )}
            </div>

            {/* Gift Info */}
            {pass.isGifted === 1 && (
              <div className="bg-zinc-800/70 border border-dashed border-lime-500 rounded-xl p-4 mb-6 text-sm">
                <p className="text-lime-400">Gifted by: <strong>{pass.gifterName}</strong></p>
                {pass.giftMessage && <p className="mt-2 italic">"{pass.giftMessage}"</p>}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={() => handleInstantReferralsListClick(pass.passId)}
                className="flex-1 sm:flex-none bg-purple-700 hover:bg-purple-600 transition px-5 py-3 rounded-xl text-xs lg:text-sm flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faUserClock} />
                Instant Referrals
              </button>

              <button
                onClick={() => handleViewBillClick(pass.passId)}
                className="flex-1 sm:flex-none bg-emerald-700 hover:bg-emerald-600 transition px-5 py-3 rounded-xl text-xs lg:text-sm flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                Bill / Receipt
              </button>
            </div>

            {/* Instant Referrals Section */}
            {instantReferralViewClicked && (
              <div className="mt-6 bg-zinc-950 border border-zinc-700 rounded-xl p-4 overflow-x-auto">
                <h4 className="font-semibold mb-3 text-blue-400">Instant Referral List</h4>
                {myBBList.length > 0 ? (
                  <table className="w-full text-xs lg:text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-2">User</th>
                        <th className="text-right py-2">Bonus (₹)</th>
                        <th className="text-right py-2">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myBBList.map((item, i) => (
                        <tr key={i} className="border-b border-zinc-800 last:border-0">
                          <td className="py-3">{item.userName}</td>
                          <td className="py-3 text-right text-emerald-400">
                            {(item.paymentAmount * 0.13).toFixed(2)}
                          </td>
                          <td className="py-3 text-right text-zinc-400">{formatDateTime(item.purchaseDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-zinc-400 text-center py-4">No instant referrals yet.</p>
                )}
              </div>
            )}

            {/* Bill Section */}
            {viewBillClicked && myInvoice && (
              <div className="mt-6 bg-zinc-950 border border-zinc-700 rounded-xl p-5">
                <h4 className="font-semibold mb-3 text-emerald-400">Bill cum Receipt</h4>
                <div
                  className="prose prose-invert max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: myInvoice.invoice }}
                />
              </div>
            )}

            {/* MLM Section */}
            {pass.buyingMethod === "MLM" && (
              <div className="mt-8 pt-6 border-t border-zinc-700">
                <p className="text-yellow-200 text-sm lg:text-base leading-relaxed mb-5">
                  You are eligible for <strong>ValueShare / Multi-Level Referral</strong> program.
                </p>

                <button
                  onClick={() => setShowTable(!showTable)}
                  className="w-full bg-blue-600 hover:bg-blue-500 transition py-3 rounded-xl text-sm font-medium mb-6"
                >
                  {showTable ? "Hide" : "View"} Referral Income Chart
                </button>

                {showTable && <ExamUnionEarningCalculator param={pass.salesPrice} />}

                {/* Referrer Info */}
                <div className="my-6 text-center text-sm">
                  My Referrer: <span className="text-yellow-300 font-medium">{pass.advisorName}</span>
                </div>

                {/* Direct Referrals */}
                <button
                  onClick={() => handleMyReferralsListClick(pass)}
                  className="w-full bg-purple-700 hover:bg-purple-600 py-3 rounded-xl text-sm font-medium mb-4"
                >
                  {showReferralsList ? "Hide" : "Show"} Direct Referrals List
                </button>

                {showReferralsList && selectedPassId === pass.passId && (
                  <div className="bg-zinc-950 p-4 rounded-xl text-xs lg:text-sm space-y-3">
                    {myReferralsList.length === 0 ? (
                      <p className="text-red-400 text-center py-6">No direct referrals yet for this pass.</p>
                    ) : (
                      myReferralsList.map((ref, i) => (
                        <div key={i} className="bg-zinc-900 p-3 rounded-lg border border-zinc-700">
                          <p><span className="text-zinc-400">User:</span> {ref.userName} ({ref.userCode})</p>
                          <p><span className="text-zinc-400">Date:</span> {formatDate(ref.purchaseDate)}</p>
                          <p><span className="text-zinc-400">Price:</span> ₹{ref.salesPrice?.toFixed(2)}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Down The Line Referrals */}
                <div className="mt-8">
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white text-sm flex-1"
                    >
                      {PERIOD_OPTIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>

                    <select
                      value={selectedLevel}
                      onChange={(e) => setSelectedLevel(e.target.value)}
                      className="bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white text-sm"
                    >
                      <option value="All">All Levels</option>
                      {[1,2,3,4,5,6,7,8].map(l => (
                        <option key={l} value={l}>Level {l}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={() => handleGetDownTheLineReferralList(pass.passId)}
                    disabled={downTheLineReferralsDataClicked}
                    className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 transition py-3.5 rounded-xl text-sm font-medium"
                  >
                    Show My Full ValueShare Network
                  </button>

                  {downTheLineReferralsDataClicked && downTheLineReferralsList.length > 0 && (
                    <div className="mt-6 overflow-x-auto bg-zinc-950 rounded-xl p-4">
                      <table className="w-full text-xs lg:text-sm">
                        <thead>
                          <tr className="border-b border-zinc-700">
                            <th className="text-left py-3">Level</th>
                            <th className="text-right py-3">Count</th>
                            <th className="text-right py-3">Commission (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(aggregatedLevels).map(([level, data]) => (
                            <tr key={level} className="border-b border-zinc-800">
                              <td className="py-3">Level {level}</td>
                              <td className="py-3 text-right">{data.count}</td>
                              <td className="py-3 text-right text-emerald-400">
                                {data.totalCom.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                          <tr className="font-bold bg-zinc-800 text-yellow-200">
                            <td className="py-3">TOTAL</td>
                            <td className="py-3 text-right">
                              {Object.values(aggregatedLevels).reduce((sum, i) => sum + i.count, 0)}
                            </td>
                            <td className="py-3 text-right">
                              {Object.values(aggregatedLevels).reduce((sum, i) => sum + i.totalCom, 0).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Wrapper Component
const PassShelf = () => {
  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      <PassShelfList />

      {/* Footer */}
      <footer className="bg-black border-t border-blue-500 py-6 px-4 text-center text-xs text-zinc-400 mt-12">
        <p>© 2026 Think5Whys Global DeepTech Solutions.</p>
        <div className="flex justify-center gap-6 mt-3 text-blue-400">
          <Link to="/about-us" className="hover:text-white">About</Link>
          <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
          <Link to="/terms-of-use" className="hover:text-white">Terms</Link>
          <Link to="/contact-us" className="hover:text-white">Contact</Link>
        </div>
      </footer>
    </div>
  );
};

export default PassShelf;